import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft, X, Loader2, CheckCircle2, Circle,
  AlertCircle, UserCog, GraduationCap,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { clientService } from '../../services';

/**
 * Edit Trainer Page (client panel)
 * Mirrors AddTrainerPage chrome — left rail (identity + preview + progress)
 * plus a card-form with numbered sections, segmented Status control, and a
 * sticky footer with ⌘+Enter submit. Email is read-only here.
 */

const CARD_BASE =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04)]';
const CARD_FORM =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.08)] overflow-hidden';

const PREVIEW_AVATAR_GRADIENTS = [
  'from-primary-500 to-primary-700',
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-violet-500 to-purple-600',
  'from-amber-500 to-orange-600',
  'from-cyan-500 to-sky-600',
];
const seededPreviewGradient = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) & 0x7fffffff;
  return PREVIEW_AVATAR_GRADIENTS[hash % PREVIEW_AVATAR_GRADIENTS.length];
};

const ROLE_OPTIONS = [
  { value: 'Trainer',          label: 'Trainer' },
  { value: 'Senior Trainer',   label: 'Senior Trainer' },
  { value: 'Lead Trainer',     label: 'Lead Trainer' },
  { value: 'Training Manager', label: 'Training Manager' },
];
const STATUS_OPTIONS = [
  { value: 'active',   label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const FIELD_LABELS = {
  firstName: 'First name',
  lastName: 'Last name',
  phone: 'Phone',
};

const EditTrainerPage = () => {
  const navigate = useNavigate();
  const { trainerId } = useParams();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'Trainer',
    jobTitle: '',
    department: '',
    status: 'active',
    bio: '',
    expertise: [],
    expertiseInput: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchTrainer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainerId]);

  const fetchTrainer = async () => {
    try {
      setLoading(true);
      const response = await clientService.getTrainer(trainerId);
      if (response.success) {
        const trainer = response.data;
        setFormData({
          firstName: trainer.firstName || '',
          lastName: trainer.lastName || '',
          email: trainer.email || '',
          phone: trainer.phone || '',
          role: trainer.jobTitle || 'Trainer',
          jobTitle: trainer.jobTitle || '',
          department: trainer.department || '',
          status: trainer.status || 'active',
          bio: trainer.bio || '',
          expertise: trainer.expertise || [],
          expertiseInput: '',
        });
      }
    } catch (error) {
      console.error('Error fetching trainer:', error);
      toast.error('Failed to load trainer details');
      navigate('/client/trainers');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (formData.phone && !/^\+?[\d\s\-()]+$/.test(formData.phone)) newErrors.phone = 'Invalid phone number format';
    setErrors(newErrors);
    return { valid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { valid } = validateForm();
    if (!valid) {
      toast.error('Please fix the form errors');
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await clientService.updateTrainer(trainerId, {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone,
        jobTitle: formData.jobTitle.trim() || formData.role,
        department: formData.department.trim(),
        status: formData.status,
        bio: formData.bio,
        expertise: formData.expertise,
      });

      if (response.success) {
        toast.success('Trainer updated successfully!');
        navigate(`/client/trainers/${trainerId}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to update trainer');
      console.error('Error updating trainer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => navigate(`/client/trainers/${trainerId}`);

  const handleAddExpertise = () => {
    if (formData.expertiseInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        expertise: [...prev.expertise, prev.expertiseInput.trim()],
        expertiseInput: '',
      }));
    }
  };
  const handleRemoveExpertise = (index) => {
    setFormData((prev) => ({
      ...prev,
      expertise: prev.expertise.filter((_, i) => i !== index),
    }));
  };

  // ⌘+Enter / Ctrl+Enter submit
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !isSubmitting) {
        e.preventDefault();
        formRef.current?.requestSubmit?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSubmitting]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      </DashboardLayout>
    );
  }

  // Derived render values
  const sectionDone = {
    personal: formData.firstName.trim() !== '' && formData.lastName.trim() !== '',
    role: !!formData.role && !!formData.status,
    profile: formData.bio.trim() !== '' || formData.expertise.length > 0,
  };

  const initials = `${(formData.firstName || '').charAt(0)}${(formData.lastName || '').charAt(0)}`.toUpperCase();
  const previewGradient = seededPreviewGradient(initials || 'AA');
  const fullName = `${formData.firstName} ${formData.lastName}`.trim();

  const errorKeys = Object.keys(errors).filter((k) => !!errors[k]);
  const errorLabelList = errorKeys.map((k) => FIELD_LABELS[k] || k).join(', ');

  return (
    <DashboardLayout>
      <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)] gap-6 w-full px-4 lg:px-6 py-6">
        {/* ────── LEFT RAIL ────── */}
        <aside className="lg:sticky lg:top-6 lg:self-start space-y-4">
          {/* Page identity */}
          <div className={`${CARD_BASE} p-5`}>
            <span className="inline-flex w-10 h-10 rounded-xl items-center justify-center bg-primary-50 ring-1 ring-inset ring-primary-100 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400 dark:ring-primary-500/20">
              <UserCog size={18} strokeWidth={1.75} />
            </span>
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-50 mt-3">Edit trainer</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Update profile details, role, and expertise.</p>
            <button
              type="button"
              onClick={() => navigate(`/client/trainers/${trainerId}`)}
              className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 inline-flex items-center gap-1 mt-4 transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.75} />
              Back to Trainer Profile
            </button>
          </div>

          {/* Live preview */}
          <div className={`${CARD_BASE} overflow-hidden`}>
            <div className="bg-gradient-to-r from-neutral-50 to-white dark:from-neutral-900 dark:to-neutral-900/60 px-4 py-2 text-[10px] uppercase tracking-[0.08em] font-semibold text-neutral-400 border-b border-neutral-100 dark:border-neutral-800/70">
              Preview
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                {initials ? (
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${previewGradient} flex items-center justify-center text-white font-semibold text-sm shadow-sm`}>
                    {initials}
                  </div>
                ) : (
                  <div className="w-11 h-11 rounded-xl border-2 border-dashed border-neutral-200 dark:border-neutral-700 flex items-center justify-center text-neutral-300 dark:text-neutral-600 text-xs font-semibold">
                    AA
                  </div>
                )}
                <div className="min-w-0">
                  <p className={`text-sm font-semibold truncate ${fullName ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-400'}`}>
                    {fullName || 'Full name'}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{formData.email || '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset bg-cyan-50 text-cyan-700 ring-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-300 dark:ring-cyan-500/30">
                  {formData.role}
                </span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset capitalize ${
                  formData.status === 'active'
                    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30'
                    : 'bg-neutral-50 text-neutral-700 ring-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-700'
                }`}>
                  {formData.status}
                </span>
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">{formData.department || '—'}</p>
            </div>
          </div>

          {/* Progress */}
          <div className={`${CARD_BASE} p-4`}>
            <p className="text-[11px] uppercase tracking-[0.06em] font-semibold text-neutral-400 mb-2">Progress</p>
            {[
              { key: 'personal', label: 'Personal information', done: sectionDone.personal },
              { key: 'role',     label: 'Role & status',         done: sectionDone.role },
              { key: 'profile',  label: 'Trainer profile',       done: sectionDone.profile },
            ].map(({ key, label, done }) => (
              <div key={key} className="flex items-center gap-2 py-1.5">
                {done
                  ? <CheckCircle2 size={14} className="text-emerald-500" />
                  : <Circle size={14} className="text-neutral-300 dark:text-neutral-600" />}
                <span className={`text-sm ${done ? 'text-neutral-700 dark:text-neutral-200' : 'text-neutral-400'}`}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </aside>

        {/* ────── MAIN FORM ────── */}
        <main>
          <form ref={formRef} onSubmit={handleSubmit} className={CARD_FORM}>
            {/* Header */}
            <div className="px-6 lg:px-8 pt-6 pb-5 border-b border-neutral-100 dark:border-neutral-800/70">
              <h1 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Edit trainer</h1>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Update the details below — required fields are marked with <span className="text-rose-500">*</span>.</p>
            </div>

            <div className="p-6 lg:p-8 space-y-10">
              {/* Error summary */}
              {errorKeys.length > 0 && (
                <div className="rounded-xl bg-rose-50 ring-1 ring-inset ring-rose-200 px-4 py-3 flex items-start gap-3 dark:bg-rose-500/10 dark:ring-rose-500/20">
                  <AlertCircle size={18} strokeWidth={1.75} className="text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-rose-900 dark:text-rose-200">Please fix the errors below</p>
                    <p className="text-xs text-rose-700/80 dark:text-rose-300/80 mt-0.5">{errorLabelList}</p>
                  </div>
                </div>
              )}

              {/* ── 01 Personal information ── */}
              <section>
                <SectionHeader step="01" title="Personal information" subtitle="Basic identity details for the trainer." />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="Enter first name" required fullWidth error={errors.firstName} />
                  <Input label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Enter last name" required fullWidth error={errors.lastName} />

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">
                      Email
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      disabled
                      fullWidth
                      className="bg-neutral-100 dark:bg-neutral-800/50"
                    />
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Email cannot be changed</p>
                  </div>

                  <Input
                    label="Phone Number"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 123-4567"
                    fullWidth
                    error={errors.phone}
                  />
                </div>
              </section>

              <div className="border-t border-neutral-100 dark:border-neutral-800/60" />

              {/* ── 02 Role & status ── */}
              <section>
                <SectionHeader step="02" title="Role & status" subtitle="What this trainer does in your organization." />
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">
                        Role <span className="text-rose-500">*</span>
                      </label>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition appearance-none cursor-pointer"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.625rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.25em 1.25em',
                          paddingRight: '2.25rem',
                        }}
                      >
                        {ROLE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <Input label="Job Title" name="jobTitle" value={formData.jobTitle} onChange={handleChange} placeholder="e.g., Senior Facilitator" fullWidth />
                  </div>

                  <Input label="Department" name="department" value={formData.department} onChange={handleChange} placeholder="e.g., Leadership Development, Sales Training" fullWidth />

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">
                      Status <span className="text-rose-500">*</span>
                    </label>
                    <div
                      className="inline-flex items-center p-1 rounded-xl bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800"
                      role="group"
                      aria-label="Status"
                    >
                      {STATUS_OPTIONS.map((opt) => {
                        const active = formData.status === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, status: opt.value }))}
                            aria-pressed={active}
                            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${
                              active
                                ? 'bg-white dark:bg-neutral-900 text-primary-700 dark:text-primary-300 shadow-sm ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700'
                                : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </section>

              <div className="border-t border-neutral-100 dark:border-neutral-800/60" />

              {/* ── 03 Trainer profile ── */}
              <section>
                <SectionHeader step="03" title="Trainer profile" subtitle="Bio and expertise areas." />
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">Bio</label>
                    <textarea
                      name="bio"
                      value={formData.bio}
                      onChange={handleChange}
                      placeholder="Brief description about the trainer…"
                      rows={3}
                      className="w-full px-3 py-2.5 text-sm rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">Areas of Expertise</label>
                    <div className="flex gap-2">
                      <Input
                        name="expertiseInput"
                        value={formData.expertiseInput}
                        onChange={handleChange}
                        placeholder="e.g., Leadership, Sales"
                        fullWidth
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddExpertise();
                          }
                        }}
                      />
                      <Button type="button" variant="secondary" onClick={handleAddExpertise}>Add</Button>
                    </div>
                    {formData.expertise.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {formData.expertise.map((exp, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 ring-1 ring-inset ring-neutral-200 bg-white px-2 py-0.5 rounded-full text-xs text-neutral-700 dark:bg-neutral-900 dark:ring-neutral-800 dark:text-neutral-200"
                          >
                            {exp}
                            <button
                              type="button"
                              onClick={() => handleRemoveExpertise(index)}
                              aria-label={`Remove ${exp}`}
                              className="text-neutral-400 hover:text-rose-600 ml-1 transition-colors"
                            >
                              <X size={12} strokeWidth={2} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* Info note */}
              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-inset ring-emerald-200/60 dark:ring-emerald-500/20 px-4 py-3 flex items-start gap-3">
                <GraduationCap size={18} strokeWidth={1.75} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-emerald-800 dark:text-emerald-200">
                  Changes take effect immediately — the trainer will see the updated profile on their next refresh.
                </p>
              </div>
            </div>

            {/* Sticky footer */}
            <div className="sticky bottom-0 z-10 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md border-t border-neutral-100 dark:border-neutral-800/70 px-6 lg:px-8 py-4 flex items-center justify-between gap-3">
              <p className="text-xs text-neutral-500 dark:text-neutral-400 hidden sm:flex items-center gap-1.5">
                <kbd className="inline-flex items-center px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700 text-[10px] font-mono text-neutral-600 dark:text-neutral-300">⌘ Enter</kbd>
                to save
              </p>
              <div className="flex items-center gap-2 ml-auto">
                <Button variant="ghost" type="button" onClick={handleCancel} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <><Loader2 size={14} className="animate-spin mr-1.5" /> Saving…</>
                  ) : (
                    <><CheckCircle2 size={14} className="mr-1.5" /> Save Changes</>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </main>
      </div>
    </DashboardLayout>
  );
};

const SectionHeader = ({ step, title, subtitle }) => (
  <div className="flex items-center gap-3 mb-5">
    <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-primary-50 dark:bg-primary-500/10 ring-1 ring-inset ring-primary-100 dark:ring-primary-500/20 text-primary-700 dark:text-primary-300 text-xs font-semibold tracking-wider">
      {step}
    </span>
    <div>
      <h2 className="text-[15px] font-semibold text-neutral-900 dark:text-neutral-100 leading-tight">{title}</h2>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{subtitle}</p>
    </div>
  </div>
);

export default EditTrainerPage;
